import { useState } from "react";
import { toast } from "sonner";
import { History, Star } from "lucide-react";
import { api, formatApiError } from "../../../booking/api";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Textarea } from "../../../components/ui/textarea";
import { Button } from "../../../components/ui/button";

const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][((n % 100) - 20) % 10] || ["th", "st", "nd", "rd"][n % 100] || "th"}`;

export const GuestHistory = ({ booking, onNoteSaved }) => {
  const h = booking.history || { visits: 0, recent: [], guest_note: "" };
  const [note, setNote] = useState(h.guest_note);
  const [saving, setSaving] = useState(false);
  const regular = h.visits > 0;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/api/admin/guests/note", { email: booking.email, phone: booking.phone, note });
      toast.success("Guest note saved");
      onNoteSaved?.(note);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover onOpenChange={(o) => o && setNote(h.guest_note)}>
      <PopoverTrigger asChild>
        <button data-testid={`cal-guest-${booking.code}`} className="font-semibold text-left hover:underline flex items-start gap-1 min-w-0 break-words">
          {regular && <Star size={11} className="fill-amber-400 text-amber-400 shrink-0 mt-0.5" />}
          <span className="break-words">{booking.name}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs" align="start" data-testid={`guest-history-${booking.code}`}>
        <p className="font-semibold text-sm">{booking.name}</p>
        <p className="text-stone-500 mb-3" data-testid={`guest-visits-${booking.code}`}>
          {regular ? `Returning guest · ${ordinal(h.visits + 1)} visit` : "First visit"}
        </p>
        {h.recent.length > 0 && (
          <div className="space-y-1.5 mb-3">
            <p className="flex items-center gap-1 font-medium"><History size={12} /> Past visits</p>
            {h.recent.map((v, i) => (
              <div key={i} className="border-l-2 border-[#b09873] pl-2">
                <span className="font-medium">{v.date}</span> · {v.resource_unit || v.service_name}
                {(v.notes || v.internal_notes) && <p className="text-amber-700">{[v.notes, v.internal_notes].filter(Boolean).join(" · ")}</p>}
              </div>
            ))}
          </div>
        )}
        <p className="font-medium mb-1">Staff note about this guest</p>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3}
          placeholder="e.g. Loves front row, Aperol spritz, birthday 12/7" data-testid={`guest-note-input-${booking.code}`} />
        <Button size="sm" className="mt-2 w-full" onClick={save} disabled={saving} data-testid={`guest-note-save-${booking.code}`}>Save note</Button>
      </PopoverContent>
    </Popover>
  );
};
